import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ChefHat, AlertTriangle, AlertCircle, Info, Shield, Trash2, Copy, RefreshCw, Filter, ArrowLeft, ImageOff, ListX } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const SEVERITY_CONFIG = {
  CRITICAL: { color: 'bg-red-100 text-red-800 border-red-200', icon: AlertTriangle },
  MEDIUM: { color: 'bg-amber-100 text-amber-800 border-amber-200', icon: AlertCircle },
  LOW: { color: 'bg-blue-100 text-blue-800 border-blue-200', icon: Info },
  NONE: { color: 'bg-green-100 text-green-800 border-green-200', icon: Shield },
};

const ISSUE_LABELS = {
  no_ingredients: 'No Ingredients',
  no_instructions: 'No Instructions',
  duplicate_recipe: 'Duplicate Recipe',
  missing_name: 'Missing Name',
  duplicate_ingredients: 'Duplicate Ingredients',
  few_ingredients: 'Few Ingredients',
  few_instructions: 'Few Instructions',
  missing_country: 'Missing Country',
  missing_image: 'Missing Image',
};

const ISSUE_SEVERITY = {
  no_ingredients: 'CRITICAL', no_instructions: 'CRITICAL', duplicate_recipe: 'CRITICAL', missing_name: 'CRITICAL',
  duplicate_ingredients: 'MEDIUM', few_ingredients: 'MEDIUM', few_instructions: 'MEDIUM',
  missing_country: 'LOW', missing_image: 'LOW',
};

export default function AdminRecipeHealthPage() {
  const [results, setResults] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [issuesOnly, setIssuesOnly] = useState(true);
  const [issueFilter, setIssueFilter] = useState('');
  const [actionLoading, setActionLoading] = useState({});
  const navigate = useNavigate();

  const getHeaders = () => {
    const token = localStorage.getItem('admin_token');
    if (!token) { navigate('/admin/login'); return {}; }
    return { Authorization: `Bearer ${token}` };
  };

  const fetchAudit = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '300', has_issues_only: issuesOnly });
      if (issueFilter) params.append('issue_filter', issueFilter);
      const { data } = await axios.get(`${API_URL}/api/admin/recipe-health/audit?${params}`, { headers: getHeaders() });
      setResults(data.results || []);
      setSummary({
        total_recipes: data.total_recipes,
        total_with_issues: data.total_with_issues,
        issue_type_counts: data.issue_type_counts || {},
        severity_counts: data.severity_counts || {},
        duplicate_recipe_groups: data.duplicate_recipe_groups || [],
      });
    } catch (err) {
      if (err.response?.status === 401) navigate('/admin/login');
      else toast.error('Failed to load audit');
    } finally { setLoading(false); }
  }, [issuesOnly, issueFilter]);

  useEffect(() => { fetchAudit(); }, [fetchAudit]);

  const runAction = async (slug, action, label, method = 'post') => {
    setActionLoading(p => ({ ...p, [`${slug}-${action}`]: true }));
    try {
      const fn = method === 'delete' ? axios.delete : axios.post;
      await fn(`${API_URL}/api/admin/recipe-health/${action}/${slug}?dry_run=0`, method === 'delete' ? { headers: getHeaders() } : {}, method === 'delete' ? undefined : { headers: getHeaders() });
      toast.success(`${label} — ${slug}`);
      fetchAudit();
    } catch (err) {
      toast.error(`Failed: ${err.response?.data?.detail || err.message}`);
    } finally { setActionLoading(p => ({ ...p, [`${slug}-${action}`]: false })); }
  };

  const SeverityBadge = ({ severity }) => {
    const cfg = SEVERITY_CONFIG[severity] || SEVERITY_CONFIG.NONE;
    const Icon = cfg.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium border ${cfg.color}`} data-testid={`severity-${severity?.toLowerCase()}`}>
        <Icon className="w-3 h-3" /> {severity}
      </span>
    );
  };

  const IssueBadge = ({ issue }) => {
    const sev = ISSUE_SEVERITY[issue] || 'LOW';
    const cfg = SEVERITY_CONFIG[sev];
    return <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${cfg.color}`}>{ISSUE_LABELS[issue] || issue}</span>;
  };

  return (
    <>
      <Helmet><meta name="robots" content="noindex, nofollow" /><title>Recipe Health - Admin</title></Helmet>
      <div className="min-h-screen bg-gray-50" data-testid="recipe-health-page">
        <header className="bg-white shadow-sm border-b">
          <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Link to="/admin/recipes" className="text-gray-500 hover:text-gray-700"><ArrowLeft className="w-5 h-5" /></Link>
              <ChefHat className="w-7 h-7 text-orange-700" />
              <h1 className="text-lg font-bold text-gray-800">Recipe Health Audit</h1>
            </div>
            <Button variant="outline" size="sm" onClick={fetchAudit} disabled={loading} data-testid="refresh-btn">
              <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
          </div>
        </header>

        <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
          {/* Summary */}
          {summary && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="summary-cards">
              <div className="bg-white rounded-lg border p-4 text-center shadow-sm">
                <div className="text-2xl font-bold text-gray-900">{summary.total_recipes}</div>
                <div className="text-xs text-gray-500 mt-1">Total Recipes</div>
              </div>
              <div className="bg-white rounded-lg border p-4 text-center shadow-sm">
                <div className="text-2xl font-bold text-red-600">{summary.total_with_issues}</div>
                <div className="text-xs text-gray-500 mt-1">With Issues</div>
              </div>
              <div className="bg-white rounded-lg border p-4 text-center shadow-sm">
                <div className="text-2xl font-bold text-red-700">{summary.severity_counts?.CRITICAL || 0}</div>
                <div className="text-xs text-gray-500 mt-1">Critical</div>
              </div>
              <div className="bg-white rounded-lg border p-4 text-center shadow-sm">
                <div className="text-2xl font-bold text-amber-600">{summary.severity_counts?.MEDIUM || 0}</div>
                <div className="text-xs text-gray-500 mt-1">Medium</div>
              </div>
            </div>
          )}

          {/* Duplicate groups */}
          {summary?.duplicate_recipe_groups?.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4" data-testid="duplicate-groups">
              <h3 className="text-sm font-semibold text-red-800 mb-2 flex items-center gap-1"><Copy className="w-4 h-4" /> Duplicate Recipes</h3>
              {summary.duplicate_recipe_groups.map((g, i) => (
                <div key={i} className="text-xs text-red-700 mb-1">
                  <span className="font-medium">{g.name}</span>: {g.slugs.join(', ')}
                </div>
              ))}
            </div>
          )}

          {/* Issue breakdown */}
          {summary?.issue_type_counts && Object.keys(summary.issue_type_counts).length > 0 && (
            <div className="bg-white rounded-lg border shadow-sm">
              <div className="px-4 py-2 border-b"><h3 className="text-sm font-semibold text-gray-800">Issue Breakdown</h3></div>
              <div className="px-4 py-3 flex flex-wrap gap-2" data-testid="issue-breakdown">
                {Object.entries(summary.issue_type_counts).sort((a,b) => b[1]-a[1]).map(([type, count]) => (
                  <button
                    key={type}
                    onClick={() => setIssueFilter(issueFilter === type ? '' : type)}
                    className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium border transition-all cursor-pointer ${
                      issueFilter === type ? 'bg-gray-800 text-white border-gray-800' : `${SEVERITY_CONFIG[ISSUE_SEVERITY[type] || 'LOW'].color} hover:opacity-80`
                    }`}
                    data-testid={`filter-${type}`}
                  >
                    {ISSUE_LABELS[type] || type}: {count}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Filters */}
          <div className="flex items-center gap-3" data-testid="filters">
            <Button variant={issuesOnly ? 'default' : 'outline'} size="sm" onClick={() => setIssuesOnly(!issuesOnly)} data-testid="toggle-issues-only">
              <Filter className="w-4 h-4 mr-1" /> {issuesOnly ? 'Issues Only' : 'Showing All'}
            </Button>
            {issueFilter && (
              <Badge variant="secondary" className="cursor-pointer" onClick={() => setIssueFilter('')}>
                {ISSUE_LABELS[issueFilter] || issueFilter} &times;
              </Badge>
            )}
            <span className="text-sm text-gray-500">{results.length} recipe(s)</span>
          </div>

          {/* Results */}
          {loading ? (
            <div className="text-center py-12 text-gray-500" data-testid="loading-state">Loading...</div>
          ) : results.length === 0 ? (
            <div className="text-center py-12 text-gray-400" data-testid="empty-state">
              {issuesOnly ? 'No issues found.' : 'No recipes.'}
            </div>
          ) : (
            <div className="space-y-2" data-testid="results-list">
              {results.map(r => (
                <div key={r.slug} className="bg-white rounded-lg border shadow-sm px-4 py-3" data-testid={`recipe-row-${r.slug}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-gray-900 text-sm truncate" data-testid="recipe-name">{r.recipe_name || r.slug}</span>
                        <SeverityBadge severity={r.severity} />
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5 font-mono" data-testid="recipe-slug">{r.slug}</div>
                      <div className="text-xs text-gray-500">
                        {r.details?.ingredient_count ?? '?'} ingredients / {r.details?.instruction_count ?? '?'} instructions
                        {r.origin_country && <> / {r.origin_country}</>}
                      </div>
                    </div>
                  </div>

                  {/* Issues */}
                  {r.issues?.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2" data-testid="recipe-issues">
                      {r.issues.map(iss => <IssueBadge key={iss} issue={iss} />)}
                    </div>
                  )}

                  {/* Duplicate ingredient details */}
                  {r.details?.duplicate_ingredients && (
                    <div className="mt-1.5 text-xs text-amber-700">
                      Duplicates: {r.details.duplicate_ingredients.map(d => `${d.item} (x${d.count})`).join(', ')}
                    </div>
                  )}

                  {/* Duplicate recipe slugs */}
                  {r.details?.duplicate_slugs && (
                    <div className="mt-1.5 text-xs text-red-700">
                      Also exists as: {r.details.duplicate_slugs.filter(s => s !== r.slug).join(', ')}
                    </div>
                  )}

                  {/* Actions */}
                  {r.issues?.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2 pt-2 border-t" data-testid="recipe-actions">
                      {r.issues.includes('duplicate_ingredients') && (
                        <Button variant="outline" size="sm" className="text-xs h-7 border-amber-300 text-amber-700 hover:bg-amber-50"
                          onClick={() => runAction(r.slug, 'dedup-ingredients', 'Deduplicated')}
                          disabled={actionLoading[`${r.slug}-dedup-ingredients`]}
                          data-testid={`action-dedup-${r.slug}`}
                        >
                          <ListX className="w-3 h-3 mr-1" /> Remove Duplicates
                        </Button>
                      )}
                      {r.issues.includes('duplicate_recipe') && (
                        <Button variant="outline" size="sm" className="text-xs h-7 border-red-300 text-red-700 hover:bg-red-50"
                          onClick={() => {
                            if (window.confirm(`Delete recipe "${r.slug}"? This cannot be undone.`)) {
                              runAction(r.slug, 'delete-recipe', 'Deleted', 'delete');
                            }
                          }}
                          disabled={actionLoading[`${r.slug}-delete-recipe`]}
                          data-testid={`action-delete-${r.slug}`}
                        >
                          <Trash2 className="w-3 h-3 mr-1" /> Delete This Duplicate
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
